import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, PhoneIcon, MailIcon, ClockIcon, SendIcon } from 'lucide-react';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.8,
    ease: 'easeOut'
  }
};
export function Contact() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    // Simulate API call
    setTimeout(() => {
      setFormStatus('success');
      // Reset form after 3 seconds
      setTimeout(() => setFormStatus('idle'), 3000);
    }, 1500);
  };
  return <div className="w-full pt-24 bg-brand-offwhite" data-id="element-132">
      {/* Page Header */}
      <section className="relative py-20 bg-brand-charcoal text-white overflow-hidden" data-id="element-133">
        <div className="absolute inset-0 z-0 opacity-20" data-id="element-134">
          <img src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&q=80&w=2000" alt="Corporate office meeting" className="w-full h-full object-cover" data-id="element-135" />
        </div>
        <div className="relative z-10 max-w-container mx-auto px-6 text-center" data-id="element-136">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="font-serif text-5xl md:text-6xl font-bold mb-6" data-id="element-137">
            Contact Us
          </motion.h1>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-xl text-gray-300 max-w-2xl mx-auto" data-id="element-138">
            Get in touch to schedule a consultation or inquire about our legal
            services.
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24" data-id="element-139">
        <div className="max-w-container mx-auto px-6" data-id="element-140">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-141">
            {/* Contact Information */}
            <motion.div {...fadeInUp} data-id="element-142">
              <h2 className="font-serif text-4xl font-bold text-brand-green mb-8" data-id="element-143">
                Get In Touch
              </h2>

              <div className="space-y-8 mb-12" data-id="element-144">
                <div className="flex items-start" data-id="element-145">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm shrink-0 mr-4 border border-gray-100" data-id="element-146">
                    <MapPinIcon className="w-5 h-5 text-brand-gold" data-id="element-147" />
                  </div>
                  <div data-id="element-148">
                    <h3 className="font-serif text-xl font-bold text-brand-charcoal mb-2" data-id="element-149">
                      Our Offices
                    </h3>
                    <p className="text-gray-600 leading-relaxed" data-id="element-150">
                      Plot 70667, 7th Floor
                      <br data-id="element-151" />
                      The Tower, Farscape Precinct
                      <br data-id="element-152" />
                      Fairgrounds Office Park
                      <br data-id="element-153" />
                      Gaborone, Botswana
                    </p>
                    <p className="text-gray-600 mt-2" data-id="element-154">
                      <strong data-id="element-155">Postal:</strong> P.O. Box 612 AAH, Masa, Gaborone
                    </p>
                  </div>
                </div>

                <div className="flex items-start" data-id="element-156">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm shrink-0 mr-4 border border-gray-100" data-id="element-157">
                    <PhoneIcon className="w-5 h-5 text-brand-gold" data-id="element-158" />
                  </div>
                  <div data-id="element-159">
                    <h3 className="font-serif text-xl font-bold text-brand-charcoal mb-2" data-id="element-160">
                      Phone
                    </h3>
                    <p className="text-gray-600" data-id="element-161">
                      <strong data-id="element-162">Office:</strong>{' '}
                      <a href="tel:+2673165472" className="text-brand-green hover:text-brand-gold transition-colors" data-id="element-163">
                        (+267) 316 5472
                      </a>
                      <br data-id="element-164" />
                      <strong data-id="element-165">Mobile/WhatsApp:</strong>{' '}
                      <a href="tel:+26772553210" className="text-brand-green hover:text-brand-gold transition-colors" data-id="element-166">
                        (+267) 7255 3210
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start" data-id="element-167">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm shrink-0 mr-4 border border-gray-100" data-id="element-168">
                    <MailIcon className="w-5 h-5 text-brand-gold" data-id="element-169" />
                  </div>
                  <div data-id="element-170">
                    <h3 className="font-serif text-xl font-bold text-brand-charcoal mb-2" data-id="element-171">
                      Email
                    </h3>
                    <a href="mailto:charles@mcerickattorneys.co.bw" className="text-brand-green hover:text-brand-gold transition-colors" data-id="element-172">
                      charles@mcerickattorneys.co.bw
                    </a>
                  </div>
                </div>

                <div className="flex items-start" data-id="element-173">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm shrink-0 mr-4 border border-gray-100" data-id="element-174">
                    <ClockIcon className="w-5 h-5 text-brand-gold" data-id="element-175" />
                  </div>
                  <div data-id="element-176">
                    <h3 className="font-serif text-xl font-bold text-brand-charcoal mb-2" data-id="element-177">
                      Office Hours
                    </h3>
                    <p className="text-gray-600" data-id="element-178">
                      Monday - Friday: 08:00 - 17:00
                      <br data-id="element-179" />
                      Weekends & Public Holidays: Closed
                    </p>
                  </div>
                </div>
              </div>

              {/* Interactive Google Map */}
              <div className="w-full h-64 rounded-2xl overflow-hidden shadow-soft border border-gray-100" data-id="element-180">
                <iframe title="McErick Attorneys office location — Fairgrounds Office Park, Gaborone" src="https://www.google.com/maps?q=Fairgrounds%20Office%20Park%2C%20Gaborone%2C%20Botswana&output=embed" width="100%" height="100%" style={{
                border: 0
              }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-181"></iframe>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div {...fadeInUp} className="bg-white p-8 md:p-10 rounded-3xl shadow-card border border-gray-100" data-id="element-182">
              <h3 className="font-serif text-3xl font-bold text-brand-charcoal mb-6" data-id="element-183">
                Send us a message
              </h3>
              <p className="text-gray-600 mb-8" data-id="element-184">
                Fill out the form below and we will get back to you as soon as
                possible to discuss your legal needs.
              </p>

              {formStatus === 'success' ? <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-6 text-center h-64 flex flex-col items-center justify-center" data-id="element-185">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-186">
                    <SendIcon className="w-8 h-8 text-green-600" data-id="element-187" />
                  </div>
                  <h4 className="font-bold text-xl mb-2" data-id="element-188">Message Sent!</h4>
                  <p data-id="element-189">
                    Thank you for reaching out. We will contact you shortly.
                  </p>
                </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-190">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-191">
                    <div data-id="element-192">
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-193">
                        First Name *
                      </label>
                      <input type="text" id="firstName" required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-green/20 focus:border-brand-green outline-none transition-all bg-gray-50 focus:bg-white" placeholder="John" data-id="element-194" />
                    </div>
                    <div data-id="element-195">
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-196">
                        Last Name *
                      </label>
                      <input type="text" id="lastName" required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-green/20 focus:border-brand-green outline-none transition-all bg-gray-50 focus:bg-white" placeholder="Doe" data-id="element-197" />
                    </div>
                  </div>

                  <div data-id="element-198">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-199">
                      Email Address *
                    </label>
                    <input type="email" id="email" required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-green/20 focus:border-brand-green outline-none transition-all bg-gray-50 focus:bg-white" placeholder="john@example.com" data-id="element-200" />
                  </div>

                  <div data-id="element-201">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-202">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-green/20 focus:border-brand-green outline-none transition-all bg-gray-50 focus:bg-white" placeholder="(+267) 71 234 567" data-id="element-203" />
                  </div>

                  <div data-id="element-204">
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-205">
                      Area of Interest
                    </label>
                    <select id="service" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-green/20 focus:border-brand-green outline-none transition-all bg-gray-50 focus:bg-white appearance-none" data-id="element-206">
                      <option value="" data-id="element-207">Select a service...</option>
                      <option value="conveyancing" data-id="element-208">
                        Conveyancing & Property
                      </option>
                      <option value="family" data-id="element-209">Family Law</option>
                      <option value="corporate" data-id="element-210">Corporate & Commercial</option>
                      <option value="labour" data-id="element-211">Employment & Labour</option>
                      <option value="litigation" data-id="element-212">Civil Litigation</option>
                      <option value="other" data-id="element-213">Other</option>
                    </select>
                  </div>

                  <div data-id="element-214">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-215">
                      Message *
                    </label>
                    <textarea id="message" rows={4} required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-green/20 focus:border-brand-green outline-none transition-all bg-gray-50 focus:bg-white resize-none" placeholder="How can we help you?" data-id="element-216"></textarea>
                  </div>

                  <button type="submit" disabled={formStatus === 'submitting'} className="w-full py-4 bg-brand-green hover:bg-brand-green-dark text-white rounded-xl font-medium transition-colors flex items-center justify-center disabled:opacity-70" data-id="element-217">
                    {formStatus === 'submitting' ? <span className="flex items-center" data-id="element-218">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-219">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-220"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-221"></path>
                        </svg>
                        Sending...
                      </span> : 'Send Message'}
                  </button>
                </form>}
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}